import { useEffect, useState, useCallback } from 'react'
import { useCreateUserMutation } from 'app/services/user.service'
import CreateUserForm from './CreateUserForm'
import CustomModal from 'components/modals/CustomModal'
import { MODALS } from 'locales/locales'
import { useTranslation } from 'react-i18next'
import { emailValidation, requiredValidation } from 'utils/helpers/validations'
import { FixMeLater } from 'types'
import { useLazyGetAdminNewsfeedTeamListQuery } from 'app/services/group.service'
import { useLazyGetRolesQuery } from 'app/services/system.service'


interface ICreateUserModalProps {
  onCancel: () => void
  onClose: () => void
}

const initialUserInput = {
  fullname: '',
  email: '',
  teamId: [],
  title: '',
  isVerify: false,
  roleId: [],
}

const CreateUserModal = ({ onCancel, onClose }: ICreateUserModalProps) => {
  const { t } = useTranslation()
  const [createUser] = useCreateUserMutation()
  const [isLoading, setIsLoading] = useState(true)

  const [formChecked, setFormChecked] = useState(false)
  const [userInput, setUserInput] = useState({ ...initialUserInput })
  const [dropDownData, setDropDownData] = useState<{
    teams?: Array<FixMeLater>
    roles?: Array<FixMeLater>
  }>({roles:[], teams:[]})
  const [selectedOptions, setSelectedOptions] = useState({
    roleId: [],
    teamId: [],
  })

  // Dropdown filter state management
  const setDropdownDataState = useCallback((filter: FixMeLater) => {
    // Bu fonksiyon dropdown search için gerekli ama burada kullanmıyoruz
    //console.log('Filter state updated:', filter)
  }, [])

  const [getAdminNewsfeedTeamList] = useLazyGetAdminNewsfeedTeamListQuery()
  const [getRoles] = useLazyGetRolesQuery({refetchOnReconnect: true})

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Roles data
        const rolesData = await getRoles({ take: 999, skip: 0, sort: -1, count:0 })
        if (rolesData?.data?.data) {
          setDropDownData((prevState) => ({
            ...prevState,
            roles: rolesData.data.data.map((role: FixMeLater) => ({ value: role._id, title: role.name }))
          }));
        }
        //Teams data
        const teamsData = await getAdminNewsfeedTeamList({
          skip: 0,
          take: 10,
          sort: [],
          filter: { logic: 'and', filters: [] }
        }) as FixMeLater
        
        if (teamsData?.data?.success) {
          const mappedTeams = teamsData.data.data.map((team: FixMeLater) => ({
            title: team.name,
            value: team._id,
          }))
          setDropDownData((prevState) => ({ ...prevState, teams: mappedTeams }))
        }
      } catch (error) {
        console.error('Error loading initial data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadInitialData()
  }, [])

  const handleDropdownSearch = useCallback(async (skip: number, filter: string) => {
    try {
      const filterConfig = filter
        ? { logic: 'and', filters: [{ field: 'name', operator: 'contains', value: filter }] }
        : { logic: 'and', filters: [] }

      const adminNewsfeedTeamList = (await getAdminNewsfeedTeamList({
        skip,
        take: 10,
        filter: filterConfig,
      })) as FixMeLater

      if (adminNewsfeedTeamList?.data?.success) {
        const mappedData = adminNewsfeedTeamList.data.data.map((team: FixMeLater) => ({
          title: team.name,
          value: team._id,
        }))
        
        return {
          data: mappedData,
          total: adminNewsfeedTeamList.data.totalCount || mappedData.length,
        }
      }  
      return { data: [], total: 0 }
    } catch (error) {
      console.error('Error in handleDropdownSearch:', error)
      return { data: [], total: 0 }
    }
  }, [getAdminNewsfeedTeamList])

  const onOk = async () => {
    setFormChecked(true)
    const { isValid: isValidEmail } = emailValidation(userInput.email)
    const { isValid: isValidFullname } = requiredValidation(userInput.fullname)

    try {
      setIsLoading(true)
      if (isValidFullname && isValidEmail) {
        const res: FixMeLater = await createUser({
          ...{
            email: userInput.email?.trim(),
            fullname: userInput.fullname?.trim(),
            title: userInput.title?.trim(),
          },
          isVerify: userInput?.isVerify ?? false,
          teamIds: userInput?.teamId?.map((team:FixMeLater) => team?.value) || [],
          roleIds: userInput?.roleId?.map((role:FixMeLater) => role?.value) || [],
        })

        if (res?.data?.success) {
          onClose()
        }
      }
    } catch(e) {
      console.log(e);
      setFormChecked(true)
    } finally {
      setIsLoading(false)
    }
  }

  const onChangeInput = useCallback((value: string | boolean, key: string) => {
    setSelectedOptions((prevState) => ({ ...prevState, [key]: value }))
    setUserInput((prevState) => ({ ...prevState, [key]: value })) 
  }, [])

  const onChangeCheckbox = useCallback((isVerify: boolean) => {
    setUserInput((prevState) => ({ ...prevState, isVerify: !isVerify }))
  }, [])

  const closeModal = () => {
    setFormChecked(false)
    onClose()
  }
  return (
    <CustomModal
      closeModal={closeModal}
      title={t(MODALS.CREATE_USER_TITLE)}
      onCancel={onCancel}
      loading={isLoading}
      overflow={'visible'}
      onOk={onOk}
    >
      <CreateUserForm
        {...userInput}
        dropDownData={dropDownData}
        setDropDownData={setDropDownData}
        selectedOptions={selectedOptions}
        formChecked={formChecked}
        onChangeInput={onChangeInput}
        onChangeCheckbox={onChangeCheckbox}
        setDropdownDataState={setDropdownDataState}
        handleDropdownSearch={handleDropdownSearch}
      />
     
    </CustomModal>
  )
}

export default CreateUserModal
